import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useCart } from "../context/CartContext";

const HeaderWrapper = styled.header`
  background: rgba(4, 120, 87, 0.95);
  backdrop-filter: blur(8.9px);
  padding: 20px 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;

  &.scrolled {
    background: rgba(4, 120, 87, 0.98);
    padding: 15px 0;
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;

  img {
    height: 50px;
    width: auto;

    @media (max-width: 768px) {
      height: 40px;
    }
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: ${(props) => (props.isOpen ? "0" : "-100%")};
    width: 80%;
    height: 100vh;
    background: rgba(5, 46, 22, 0.98);
    backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: center;
    transition: left 0.3s ease;
    z-index: 999;
  }
`;

const NavLink = styled(Link)`
  color: #ffffff;
  text-decoration: none;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 1.1rem;
  padding: 12px 15px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover,
  &.active {
    color: var(--secondary-color);
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 1024px) {
    font-size: 1.3rem;
    padding: 24px;
  }
`;

const SubMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: #052e16;
  border-radius: 20px;
  padding: 20px 0;
  min-width: 220px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;

  ${NavLink}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const SubMenuLink = styled(Link)`
  display: block;
  color: #ffffff;
  text-decoration: none;
  padding: 15px 20px;
  font-size: 0.9em;
  transition: color 0.3s ease;

  &:hover {
    color: var(--secondary-color);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const QuoteButton = styled(Link)`
  background-color: var(--secondary-color);
  color: white;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--secondary-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
`;

const SearchButton = styled(Link)`
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: scale(1.1);
  }
`;

const CartButton = styled(Link)`
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-decoration: none;

  &:hover {
    transform: scale(1.1);
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(4, 120, 87, 0.95);
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const Overlay = styled.div`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;

  @media (min-width: 1025px) {
    display: none;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { getCartTotal } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const servicesSubmenu = [
    { path: "/services/new-containers", label: "New Containers" },
    { path: "/services/used-containers", label: "Used Containers" },
    { path: "/services/custom-modifications", label: "Custom Modifications" },
    { path: "/services/mobile-offices", label: "Mobile Offices" },
    { path: "/services/nationwide-delivery", label: "Nationwide Delivery" },
    {
      path: "/services/container-storage-solutions",
      label: "Container Storage Solutions",
    },
  ];

  return (
    <>
      <HeaderWrapper className={isScrolled ? "scrolled" : ""}>
        <NavContainer>
          <Logo style={{ marginRight: 15 }} to="/" onClick={closeMobileMenu}>
            <img src="/assets/2025/08/logo-wt.png" alt="Storage B" />
          </Logo>

          <NavMenu isOpen={isMobileMenuOpen}>
            <NavLink
              to="/"
              className={location.pathname === "/" ? "active" : ""}
              onClick={closeMobileMenu}
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              className={location.pathname === "/about" ? "active" : ""}
              onClick={closeMobileMenu}
            >
              About
            </NavLink>

            <NavLink
              to="/containers"
              className={location.pathname === "/containers" ? "active" : ""}
              onClick={closeMobileMenu}
            >
              Containers
            </NavLink>

            <div style={{ position: "relative" }}>
              <NavLink
                to="/services"
                className={
                  location.pathname.includes("/services") ? "active" : ""
                }
                onClick={closeMobileMenu}
              >
                Services
              </NavLink>
              <SubMenu>
                {servicesSubmenu.map((item, index) => (
                  <SubMenuLink
                    key={index}
                    to={item.path}
                    onClick={closeMobileMenu}
                  >
                    {item.label}
                  </SubMenuLink>
                ))}
              </SubMenu>
            </div>

            <NavLink
              to="/track"
              className={location.pathname === "/track" ? "active" : ""}
              onClick={closeMobileMenu}
            >
              Track
            </NavLink>

            <NavLink
              to="/contact"
              className={location.pathname === "/contact" ? "active" : ""}
              onClick={closeMobileMenu}
            >
              Contact
            </NavLink>
          </NavMenu>

          <ActionButtons>
            <QuoteButton to="/contact">Request Quote</QuoteButton>
            <CartButton to="/cart">
              <i className="fa fa-shopping-cart"></i>
              {getCartTotal() > 0 && <CartBadge>{getCartTotal()}</CartBadge>}
            </CartButton>
            <SearchButton to="/search">
              <i className="fa fa-search"></i>
            </SearchButton>
          </ActionButtons>

          <MobileMenuToggle onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? "✕" : "☰"}
          </MobileMenuToggle>
        </NavContainer>
      </HeaderWrapper>

      <Overlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

      {/* Spacer to account for fixed header */}
      <div style={{ height: "90px" }}></div>
    </>
  );
};

export default Header;
